import { applicationRoot, foreignDialogOpen, isModalGuideOpen } from '@/guidance/engine/dom';

/**
 * The Quick Find keyboard shortcut (CONTRACT §4): ⌘K on Apple platforms,
 * Ctrl K elsewhere.
 *
 * Matched by the physical key (`event.code === 'KeyK'`), never `event.key`: on
 * an Arabic layout the same key types «ن», and the shortcut must not depend on
 * the layout somebody happens to have selected.
 *
 * It does nothing — and leaves the key alone — whenever another layer owns the
 * keyboard: any modal dialog (the Help panel, a confirmation, the menu sheet,
 * Quick Find itself), a MODAL guide card (a tour step, a coach's explanation
 * or hand-over), an inert application root, an IME composition (Arabic
 * input), a repeating key, or a handler that already claimed the event. Inside
 * a text field it only ever OPENS Quick Find; it types nothing and changes
 * nothing there.
 *
 * A coach's dock is not modal: while it shows, the page — and ⌘K — stay the
 * person's (`isModalGuideOpen`, the contract with the guidance engine, PREP
 * §1.11). Quick Find then opens as any dialog does, and the coach yields to it.
 */

/** Whether this browser runs on an Apple platform, where ⌘ is the modifier. */
export function isApplePlatform(nav: Navigator | undefined = globalThis.navigator): boolean {
  if (!nav) return false;

  const hinted = (nav as Navigator & { userAgentData?: { platform?: string } }).userAgentData
    ?.platform;
  const platform = hinted ?? nav.platform ?? '';

  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** The visible hint: «⌘K» or «Ctrl K». */
export function shortcutHint(apple: boolean): string {
  return apple ? '⌘K' : 'Ctrl K';
}

/** The `aria-keyshortcuts` value for the entry. */
export function ariaKeyShortcuts(apple: boolean): string {
  return apple ? 'Meta+K' : 'Control+K';
}

export function matchesShortcut(event: KeyboardEvent, apple: boolean): boolean {
  if (event.code !== 'KeyK' || event.altKey || event.shiftKey) return false;

  return apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

/** Whether some other layer owns the keyboard right now. */
export function shortcutBlocked(event: KeyboardEvent, doc: Document = document): boolean {
  // 229: the key an IME reports while it composes (older Safari sets no isComposing).
  if (event.defaultPrevented || event.repeat || event.isComposing || event.keyCode === 229)
    return true;
  if (foreignDialogOpen(doc)) return true;
  if (isModalGuideOpen(doc)) return true;

  return applicationRoot(doc)?.hasAttribute('inert') === true;
}
